import React, { useMemo } from "react";

import { useSelector } from "react-redux";
import { Field, FormSection, getFormValues } from "redux-form";
import {
	ReduxControls,
	ErrorBanner
} from "rhombus-react";

import DynamicAssetSelect from "social/DynamicAssetSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import DynamicInput from "social/DynamicInput";
import DynamicSelect from "social/DynamicSelect";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";

import { getErrorMessagesByAdType } from "selectors/Social2";

import {
	prettifyMessages,
	mapStringToOption,
	dynamicMapStringToOption,
	dynamicNormalizeOptionToString,
	nonDynamicFields, isRequiredAssetField
} from "social/utils";
import { SNAPCHAT_CTAS } from "social/constants/CTA";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { SNAPCHAT_IMAGE_CONSTRAINTS } from "social/validators/snapchat";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { formatBytes } from "utils/common";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import SnapchatDisclaimer from "social/SnapchatDisclaimer";
import { imposiumCompositionWithoutProjectName } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";


const {
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_BRAND_NAME,
	MAX_LENGTH_LANDING_PAGE,
	MAX_IMAGE_BYTES,
	IMAGE_ACCEPT,
	DIMENSIONS,
} = SNAPCHAT_IMAGE_CONSTRAINTS;

export const SNAPCHAT_IMAGE_FIELDS = {
	isDynamic,
	assetType,
	imposiumProject,
	imposiumComposition: imposiumCompositionWithoutProjectName,
	image: {
		name: "image",
		label: "Image",
		component: DynamicAssetSelect,
		defaultDynVarName: "product",
		assetType: ASSET_TYPES.IMAGE,
		accept: IMAGE_ACCEPT,
		className: "full-width",
		required: true,
		tip: {
			direction: "top",
			description: `
				To prevent overlap, avoid placement of elements within 150px from the top<br />
				and bottom and 5% of the left and right sides of the creative. An 'AD' slug <br />
				is added on the lower right corner.
			`,
			html: true,
		},
		requirements: [
			{
				name: "File Size",
				description: `${formatBytes(MAX_IMAGE_BYTES)} Maximum`,
			},
			{
				name: "File Types",
				description: IMAGE_ACCEPT,
			},
			{
				name: "Dimensions",
				description: `${DIMENSIONS.width}x${DIMENSIONS.height} pixels`,
			},
		],
	},
	name: {
		name: "name",
		label: "Creative Name",
		component: ReduxControls.Input,
		className: "full-width",
		placeholder: "Enter a creative name",
		required: true,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
	},
	headline: {
		name: "headline",
		label: "Headline",
		component: DynamicInput,
		defaultDynVarName: "headline",
		className: "full-width",
		placeholder: "Enter a short headline",
		required: true,
		charMax: MAX_LENGTH_HEADLINE,
		// showCharCount: true,
	},
	brandName: {
		name: "brandName",
		label: "Brand Name",
		component: DynamicInput,
		defaultDynVarName: "brand_name",
		className: "full-width",
		placeholder: "Enter your brand name",
		charMax: MAX_LENGTH_BRAND_NAME,
		// showCharCount: true,
		tip: {
			direction: "right",
			description: "Brand name must match the paying Advertiser whose products or services are being advertised",
		},
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		component: DynamicInput,
		defaultDynVarName: "clickTag1",
		className: "full-width",
		placeholder: "https://www.example.com",
		required: true,
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
	},
	disclaimerText: {
		name: "disclaimerEnabled",
		component: SnapchatDisclaimer,
	},
	cta: {
		name: "cta",
		label: "CTA",
		component: DynamicSelect,
		defaultDynVarName: "cta",
		className: "half-width",
		options: SNAPCHAT_CTAS.map(mapStringToOption),
		format: dynamicMapStringToOption,
		normalize: dynamicNormalizeOptionToString,
		required: true,
		menuPlacement: "top",
		placeholder: "Select Call to Action Text",
		isClearable: false,
		variableNameInputTip: "Edit variable name",
	},
};

const SNAPCHAT_IMAGE_SETTINGS = [
	{
		name: "blockPreloading",
		label: "Block Web View Preloading",
		className: "setting-checkbox",
		component: ReduxControls.Checkbox,
		useValue: true,
		...nonDynamicFields(),
	},
	{
		name: "allowShare",
		label: "Allow Users to Share with Friends",
		className: "setting-checkbox",
		component: ReduxControls.Checkbox,
		useValue: true,
		...nonDynamicFields(),
	},
];

const adType = SOCIAL_AD_TYPES.SNAPCHAT_IMAGE;


export default function SnapchatImage(props){
	const { navigationActions, libraryId, isViewOnly, showPristineErrors, doVersionsExist } = props;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const { assetType } = formValues[adType] || {};

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, SNAPCHAT_IMAGE_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = Object.values(SNAPCHAT_IMAGE_FIELDS).map((fieldProps) => {
		const extraProps = {
		};

		if (fieldProps.name === "image") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.creativeAssetType = assetType;
			extraProps.required = isRequiredAssetField(assetType);
		}

		extraProps.disabled = isViewOnly;

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			if (fieldProps.name !== "cta") {
				extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
			}
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}
					<div className="full-width e-form__control-wrapper">
						<div className="e-form__control-header">
							<label className="e-form__control-label">Settings</label>
						</div>
						<div className="settings-fields">
							{SNAPCHAT_IMAGE_SETTINGS.map(setting => (
								<Field
									key={setting.name}
									{...setting}
									className="custom-checkbox"
									disabled={isViewOnly}
								/>
							))}
						</div>
					</div>
				</div>
			</FormSection>
		</div>
	);
}

SnapchatImage.displayName = "SnapchatImageForm";
